import React, { useRef, useState } from "react";
import { gettext, interpolate } from "../../shared/utils/legacy";
import {
  ActionType,
  DiseaseContent,
  DiseaseStatusType,
  useDiseaseStatus,
} from "../hooks/useDiseaseStatus";
import { calcPct } from "../utils";
import cropr from "../../shared/utils/cropr";
import WrapErrorLoading from "../../shared/components/WrapErrorLoading";
import cx from "classnames";
import { Overlay, Popover } from "react-bootstrap";
import DashboardSummaryInfo from "./DashboardSummaryInfo";

const actionTranslations = {
  possible: gettext("Infection events after tomorrow, check advice again"),
  killed: gettext("Crop has been killed"),
  missing: gettext("No recent observations found"),
  immediate: gettext("Immediate action required"),
  consider: gettext("Consider taking action"),
  tomorrow: gettext("Check again tomorrow"),
  nodata: gettext("No advice calculated"),
  no: gettext("No action required"),
} as { [action in ActionType]: string };

interface DiseasePartProperties {
  status: DiseaseContent;
  i: number;
  setPopover: (open: boolean) => void;
  showPopover: boolean;
  diseaseStatus: DiseaseStatusType;
}
const DiseasePart = ({
  status,
  setPopover,
  diseaseStatus,
  showPopover,
  i,
}: DiseasePartProperties) => {
  const target = useRef(null);
  const valuePct = calcPct(status.value, diseaseStatus.counter.total);
  //TODO rewrite to https://react-bootstrap.github.io/components/progress/
  return (
    <div
      ref={target}
      style={{
        backgroundColor: status.color,
        width: valuePct + "%",
      }}
      role="progressbar"
      aria-valuenow={valuePct}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cx("cursor-pointer", "dashboard-summary-tile", "progress-bar")}
      onMouseEnter={() => setPopover(true)}
      onMouseLeave={() => setPopover(false)}
      /*TODO link to filtered view */
      onClick={() => window.open("/dss/disease/", "_self")}
    >
      {status.value}
      <Overlay target={target.current} show={showPopover} placement="bottom">
        <Popover id={"diseaseStatus" + i} className={"summary_widget_popover"}>
          <Popover.Title as="h3">{actionTranslations[status.action]}</Popover.Title>
          <Popover.Content className="mh-80px overflow-auto">
            <ul className="list-group list-group-flush">
              {Object.entries(diseaseStatus.cropsPerAction[status.action] || [])
                .sort(([cropIda, cfSeta], [cropIdb, cfSetb]) => {
                  // sort by nr of cropfields, and then by name
                  if (cfSeta.size === cfSetb.size)
                    return cropr.croptype[cropIda].name < cropr.croptype[cropIdb].name ? -1 : 1;
                  return cfSeta.size < cfSetb.size ? -1 : 1;
                })
                .map(([cropId, cfSet], i) => (
                  <li key={status.action + i} className="list-group-item py-1">
                    <a
                      href={"/dss/disease/"}
                      data-metrics_category="summary_widget"
                      data-metrics_sub_category="disease_overview"
                    >
                      {cfSet.size === 1
                        ? interpolate(gettext("%s cropfield"), [cfSet.size])
                        : interpolate(gettext("%s cropfields"), [cfSet.size])}
                    </a>
                    &nbsp;({cropr.croptype[cropId].name})
                  </li>
                ))}
            </ul>
          </Popover.Content>
        </Popover>
      </Overlay>
    </div>
  );
};

function DiseaseComponent() {
  const diseaseStatus = useDiseaseStatus();
  const [popoverOpen, setPopoverOpen] = useState("");
  return (
    <WrapErrorLoading error={false}>
      {() =>
        diseaseStatus && (
          <div className={cx("dashboard-summary-tile", "progress")}>
            {diseaseStatus.data.content
              .sort((a, b) => {
                // Sort by value (numeric), and then by action type, to keep the same order in the case of identical values.
                if (a.value === b.value) return a.action > b.action ? -1 : 1;
                return a.value > b.value ? -1 : 1;
              })
              .map((status, i: number) => (
                <DiseasePart
                  diseaseStatus={diseaseStatus}
                  status={status}
                  i={i}
                  key={i}
                  setPopover={(open) => setPopoverOpen(open ? status.action : "")}
                  showPopover={popoverOpen === status.action}
                />
              ))}
          </div>
        )
      }
    </WrapErrorLoading>
  );
}
export default function DashboardSummaryDisease() {
  // Kleuren & volgorde zoals in huidige legenda
  // Hover zie je hoeveel percelen
  // - Geen ziekte? wel tonen maar upsell
  // TODO Klik op specifieke elementen in widget gaat naar gefilterde view (groep percelen)
  //
  let hasDisease =
    cropr.features.dacom_online_disease || cropr.features.dacom_online_disease_contract;
  return (
    <div className="col-4 col-md py-3">
      <h6 className="text-muted mb-3">{gettext("Disease advice")}</h6>
      {(hasDisease && <DiseaseComponent />) || (
        <DashboardSummaryInfo
          href="https://cropx.nl/producten/ziektemanagement/"
          category="disease"
        />
      )}
    </div>
  );
}
